import type { DiagnosisDetailEntry } from '@/types/productOptimization';

interface Props {
  title: string;
  entries: DiagnosisDetailEntry[];
}

const GRADE_BADGE_STYLE: Record<string, { bg: string; color: string }> = {
  적합: { bg: '#E1EDFF', color: '#4B93FF' },
  좋음: { bg: '#E1EDFF', color: '#4B93FF' },
  양호: { bg: '#E1EDFF', color: '#4B93FF' },
  개선필요: { bg: '#FFE9E9', color: '#FF5B5B' },
  불리: { bg: '#FFE9E9', color: '#FF5B5B' },
  나쁨: { bg: '#FFE9E9', color: '#FF5B5B' },
};

const FALLBACK_BADGE = { bg: '#EFEFEF', color: '#8E9096' };

/**
 * Figma 486:7805 — 섹션 타이틀 아래로 itemName 기준 서브섹션을 분리.
 * 각 서브섹션: 서브 타이틀 + currentValue 회색 박스 (존재 시) + grade 배지 + 메시지 행들.
 */
const DiagnosisSection = ({ title, entries }: Props) => {
  if (entries.length === 0) return null;

  // 순서 유지하며 연속된 같은 itemName 끼리 그룹
  const groups: { name: string; items: DiagnosisDetailEntry[] }[] = [];
  entries.forEach((entry) => {
    const last = groups[groups.length - 1];
    if (last && last.name === entry.itemName) {
      last.items.push(entry);
    } else {
      groups.push({ name: entry.itemName, items: [entry] });
    }
  });

  return (
    <section className="flex flex-col gap-[16px] rounded-[12px] border border-gr-100 bg-white p-[16px]">
      <h3 className="text-gr-800 Title3S18">{title}</h3>

      {groups.map((group, gIdx) => {
        const currentValue = group.items.find((item) => item.currentValue)?.currentValue;
        return (
          <div key={`${group.name}-${gIdx}`} className="flex flex-col gap-[12px]">
            <h4 className="text-gr-800 Body3S15">{group.name}</h4>
            {currentValue && (
              <div className="rounded-[10px] bg-[#F9F9F9] p-[10px] text-gr-800 Body5M14">
                {currentValue}
              </div>
            )}
            {group.items.map(
              (item, iIdx) =>
                item.message && (
                  <div
                    key={`${item.itemCode}-${iIdx}`}
                    className="flex items-center gap-[12px]"
                  >
                    <span
                      className="shrink-0 rounded-[4px] px-[6px] py-[2px] Caption1M12"
                      style={{
                        backgroundColor: (GRADE_BADGE_STYLE[item.grade] ?? FALLBACK_BADGE).bg,
                        color: (GRADE_BADGE_STYLE[item.grade] ?? FALLBACK_BADGE).color,
                      }}
                    >
                      {item.grade}
                    </span>
                    <span className="text-gr-700 Body5M14">{item.message}</span>
                  </div>
                ),
            )}
          </div>
        );
      })}
    </section>
  );
};

export default DiagnosisSection;
